import React, { useState } from 'react';
import { Cloud, CloudLightning, CloudSun, Sun, SunDim, ArrowRight, ChevronDown, ChevronUp, ClipboardCheck, User, Calendar } from 'lucide-react';
import { cn } from '../../lib/utils';
import { money } from '../roi/money';

// Maturity as weather: shared visuals for the story page, the PNG card and any future surface.
export const WEATHER = {
  Stormy: { Icon: CloudLightning, hex: '#7c3aed', bg: 'bg-violet-500/15 text-violet-700', ring: 'border-violet-300' },
  Overcast: { Icon: Cloud, hex: '#64748b', bg: 'bg-slate-500/15 text-slate-700', ring: 'border-slate-300' },
  'Partly cloudy': { Icon: CloudSun, hex: '#d97706', bg: 'bg-amber-500/15 text-amber-700', ring: 'border-amber-300' },
  Clear: { Icon: SunDim, hex: '#0284c7', bg: 'bg-sky-500/15 text-sky-700', ring: 'border-sky-300' },
  Sunny: { Icon: Sun, hex: '#ea580c', bg: 'bg-orange-500/15 text-orange-700', ring: 'border-orange-300' },
};
export const fmtVal = (v, unit) => (v == null ? '—' : unit === 'pts' ? `${v > 0 ? '+' : ''}${Math.round(v)} pts` : money(v, unit));

export function gapText(w) {
  if (!w || w.potential == null) return null;
  const lost = w.gap > 0;
  return `Realised ${fmtVal(w.realised, w.unit)} · clear-weather potential ${fmtVal(w.potential, w.unit)} · ${fmtVal(Math.abs(w.gap), w.unit).replace('+', '')} ${lost ? 'lost to' : 'gained from'} the weather (×${w.avg_multiplier.toFixed(2)} over the story)`;
}

export function forecastText(w) {
  const f = w?.forecast;
  if (!f) return null;
  if (f.uplift == null) return f.text;
  const lvl = f.unit === 'pts' ? Math.round(f.level) : money(f.level, f.unit);
  return `Forecast: one band brighter (${f.condition}) would lift this to ~${lvl} (${fmtVal(f.uplift, f.unit)})`;
}

// "How to earn it": the pillar's playbook moves, linked to the Maturity Health deep dive + action plan.
export function EarnIt({ w, stepKey, onOpen }) {
  const [open, setOpen] = useState(false);
  if (!w?.actions?.length) return null;
  return (
    <div className="mt-2" data-testid={`story-step-${stepKey}-actions`}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-700 hover:text-indigo-900" data-testid={`story-step-${stepKey}-actions-toggle`}>
        <ClipboardCheck className="h-3.5 w-3.5" /> How to earn it · {w.actions.length} playbook moves for {w.pillar} {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
      </button>
      {open && (
        <div className="mt-2 grid sm:grid-cols-3 gap-2">
          {w.actions.map((a, i) => (
            <div key={a.title} className="rounded-lg border border-indigo-100 bg-white p-2.5" data-testid={`story-step-${stepKey}-action-${i}`}>
              <p className="text-xs font-bold text-slate-900">{i + 1}. {a.title}</p>
              <p className="text-[11px] text-slate-600 mt-1 leading-snug">{a.description}</p>
              <p className="mt-1.5 flex flex-wrap gap-2 text-[10px] text-slate-500"><span className="inline-flex items-center gap-1"><User className="h-3 w-3" />{a.owner}</span><span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{a.horizon}</span></p>
            </div>
          ))}
          <button type="button" onClick={onOpen} className="sm:col-span-3 inline-flex items-center justify-center gap-1 rounded-md border border-indigo-200 bg-indigo-50 px-2.5 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100" data-testid={`story-step-${stepKey}-plan-open`}>Open the {w.pillar} action plan <ArrowRight className="h-3.5 w-3.5" /></button>
        </div>
      )}
    </div>
  );
}

export function WeatherChip({ w, testId }) {
  if (!w) return null;
  const m = WEATHER[w.condition] || WEATHER.Overcast;
  const b = WEATHER[w.baseline_condition];
  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold', m.bg, m.ring)} title={w.pillar} data-testid={testId}>
      {b && b !== m && <><b.Icon className="h-3.5 w-3.5 opacity-60" /><ArrowRight className="h-3 w-3 opacity-50" /></>}
      <m.Icon className="h-3.5 w-3.5" /> {w.condition} · {Math.round(w.score)} <span className="font-normal opacity-80">×{w.multiplier.toFixed(2)}</span>
    </span>
  );
}

export function WeatherBanner({ w, onOpen }) {
  if (!w) return null;
  const now = WEATHER[w.condition] || WEATHER.Overcast, base = WEATHER[w.baseline_condition] || WEATHER.Overcast;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5 grid md:grid-cols-[auto_1fr_auto] gap-4 items-center" data-testid="story-weather-banner">
      <div className="flex items-center gap-3">
        <span className={cn('h-12 w-12 rounded-2xl flex items-center justify-center', base.bg)}><base.Icon className="h-6 w-6" /></span>
        <ArrowRight className="h-4 w-4 text-slate-400" />
        <span className={cn('h-12 w-12 rounded-2xl flex items-center justify-center', now.bg)}><now.Icon className="h-6 w-6" /></span>
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">The weather · Maturity Health</p>
        <p className="text-sm md:text-base font-bold text-slate-900" data-testid="story-weather-headline">{w.baseline_condition} ({w.baseline_band} {Math.round(w.baseline_score)}) → {w.condition} ({w.band} {Math.round(w.score)}) · hand-offs now run at ×{w.multiplier.toFixed(2)}</p>
        <p className="text-xs text-slate-600 mt-1 max-w-3xl" data-testid="story-weather-text">{w.text}</p>
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="story-weather-series">
          {w.series.map((q) => { const s = WEATHER[q.condition] || WEATHER.Overcast; return <span key={q.label} className={cn('inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-semibold', s.bg, s.ring)} title={q.label}><s.Icon className="h-3 w-3" />{q.period} · {Math.round(q.score)}</span>; })}
        </div>
        {w.forecast && <div className="mt-3 rounded-xl border border-indigo-200 bg-indigo-50/60 px-3 py-2" data-testid="story-weather-forecast">
          <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-indigo-600">Forecast · one band brighter{w.forecast.band ? ` (${w.band} → ${w.forecast.band})` : ''}</p>
          {w.forecast.steps?.length > 0 ? <div className="mt-1.5 flex flex-wrap gap-1.5">{w.forecast.steps.map((u) => <span key={u.key} className="inline-flex items-center gap-1 rounded-md border border-indigo-200 bg-white px-2 py-0.5 text-[11px]" data-testid={`story-weather-forecast-${u.key}`}><span className="text-slate-500">{u.label}</span><span className="font-bold text-slate-900">{u.unit === 'pts' ? Math.round(u.level_now) : money(u.level_now, u.unit)} → {u.unit === 'pts' ? Math.round(u.level) : money(u.level, u.unit)}</span><span className="font-semibold text-emerald-600">{fmtVal(u.uplift, u.unit)}</span></span>)}</div> : <p className="text-xs text-slate-600 mt-1">{w.forecast.text}</p>}
        </div>}
      </div>
      <button type="button" onClick={onOpen} className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:border-orange-400 hover:text-orange-700 shrink-0" data-testid="story-weather-open">Open Maturity Health <ArrowRight className="h-3.5 w-3.5" /></button>
    </div>
  );
}
